import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { User, Building2, Globe, Save, Loader2, LogOut, KeyRound, Download, ShieldOff } from 'lucide-react'
import { usersApi, schoolsApi, authApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useToast } from '@/hooks/use-toast'
import { roleLabel } from '@/lib/utils'
import i18n from '@/lib/i18n'

export default function Profile() {
  const { t } = useTranslation()
  const { user, setUser, setLanguage, language, logout } = useAuthStore()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState(user?.name || '')

  const refreshUser = async () => {
    const me = await authApi.getMe()
    setUser(me)
    queryClient.invalidateQueries({ queryKey: ['me'] })
  }

  const { mutate: updateProfile, isPending } = useMutation({
    mutationFn: (data: { name?: string; preferred_language?: string }) =>
      usersApi.updateProfile(data),
    onSuccess: (updatedUser) => {
      setUser(updatedUser)
      queryClient.invalidateQueries({ queryKey: ['me'] })
      toast({ title: 'Profilo aggiornato' })
    },
    onError: () => toast({ title: 'Errore nell\'aggiornamento', variant: 'destructive' }),
  })

  const { mutate: leaveSchool, isPending: isLeaving } = useMutation({
    mutationFn: () => schoolsApi.leave(),
    onSuccess: async () => {
      await refreshUser()
      toast({ title: 'Hai lasciato l\'istituto' })
    },
    onError: (e: any) =>
      toast({
        title: e?.response?.data?.detail || 'Errore',
        variant: 'destructive',
      }),
  })

  const switchLanguage = (lang: 'it' | 'en') => {
    setLanguage(lang)
    i18n.changeLanguage(lang)
    localStorage.setItem('varia-lang', lang)
  }

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?'

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold">{t('profile.title')}</h1>

      {/* Avatar Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16">
              <AvatarImage src={user?.avatar_url} />
              <AvatarFallback className="bg-mim-blue text-white text-xl font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-lg">{user?.name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <Badge variant="outline" className="mt-1">{roleLabel(user?.role || '')}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Form */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="w-4 h-4 text-mim-blue" />
            Informazioni Personali
          </CardTitle>
          <CardDescription className="text-xs">
            Il ruolo è assegnato dal Dirigente Scolastico del tuo istituto.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="name">Nome completo</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <Button
            className="bg-mim-blue hover:bg-blue-800 text-white"
            onClick={() => updateProfile({ name, preferred_language: language })}
            disabled={isPending}
          >
            {isPending
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Save className="w-4 h-4 mr-2" />}
            {t('profile.saveChanges')}
          </Button>
        </CardContent>
      </Card>

      {/* School Association */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4 text-mim-blue" />
            {t('profile.school')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {user?.school ? (
            <>
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium">{user.school.name}</p>
                {user.school.codice_meccanografico && (
                  <p className="text-muted-foreground text-xs mt-0.5">
                    Cod. {user.school.codice_meccanografico}
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                className="text-destructive border-destructive/40 hover:bg-destructive/10"
                onClick={() => {
                  if (confirm('Lasciare l\'istituto? Se hai un ruolo privilegiato verrà rimosso.')) {
                    leaveSchool()
                  }
                }}
                disabled={isLeaving}
              >
                {isLeaving
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <LogOut className="w-4 h-4 mr-2" />}
                Lascia istituto
              </Button>
            </>
          ) : (
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
              <KeyRound className="w-4 h-4 mt-0.5 flex-shrink-0 text-mim-blue" />
              <p>
                Non risulti associato ad alcun istituto. L'associazione avviene
                <strong> automaticamente</strong> accedendo con l'account istituzionale
                del tuo istituto (dominio Google Workspace registrato dall'amministratore).
                Se il tuo istituto non è ancora attivo, contatta il supporto ValutazIA.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Language */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-mim-blue" />
            {t('profile.language')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Button
              variant={language === 'it' ? 'default' : 'outline'}
              onClick={() => switchLanguage('it')}
              className={language === 'it' ? 'bg-mim-blue text-white' : ''}
            >
              🇮🇹 Italiano
            </Button>
            <Button
              variant={language === 'en' ? 'default' : 'outline'}
              onClick={() => switchLanguage('en')}
              className={language === 'en' ? 'bg-mim-blue text-white' : ''}
            >
              🇬🇧 English
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sicurezza e dati (P8) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-mim-blue" />
            Sicurezza e dati personali
          </CardTitle>
          <CardDescription className="text-xs">
            Esporta i tuoi dati in formato leggibile da una macchina (Art. 20 GDPR)
            o chiudi tutte le sessioni attive sui tuoi dispositivi.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button variant="outline" size="sm" asChild>
            <a href={usersApi.exportUrl()} download>
              <Download className="w-4 h-4 mr-2" />
              Esporta i miei dati (JSON)
            </a>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-rosso border-rosso/40 hover:bg-rosso-light"
            onClick={async () => {
              if (!window.confirm('Disconnettere tutte le sessioni attive? Dovrai accedere di nuovo su ogni dispositivo.')) return
              try {
                await authApi.logoutAll()
              } catch {}
              logout()
              navigate('/login')
            }}
          >
            <ShieldOff className="w-4 h-4 mr-2" />
            Disconnetti tutte le sessioni
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
